/* GBA cart extension: native Symmetry rendering, one shared mutation lock.
 * Temporary checkout fallback: remove an undiscounted managed gift before checkout.
 */
(() => {
  'use strict';
  const configNode = document.getElementById('gba-cart-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
  let busy = false;
  let refreshPending = false;
  let giftSkipped = false;
  const giftNotice = 'The free gift could not be combined with your current discounts and has been removed. You can checkout without it.';
  const isProtection = item => String(item.product_id) === String(config.productId);
  const protectionLines = cart => cart.items.filter(isProtection);
  const isGift = item => item.properties?._gba_reward === 'cleaner';
  const merchandiseCount = cart => cart.items.filter(item => !isProtection(item) && !isGift(item)).reduce((sum, item) => sum + item.quantity, 0);

  async function removeManagedGifts(cart) {
    for (let attempt = 0; attempt < 20; attempt += 1) {
      const index = cart.items.findIndex(isGift);
      if (index === -1) {
        // Verify with a fresh read before allowing checkout without the gift.
        cart = await read();
        if (!cart.items.some(isGift)) return cart;
      } else {
        cart = await change(index + 1, 0);
      }
    }
    throw new Error('The free gift could not be removed. Please try again before checking out.');
  }

  // A private property separates the reward from cleaners purchased normally.
  // Shopify's existing automatic discount must make this line free.
  async function syncGift(cart) {
    if (giftSkipped) return removeManagedGifts(cart);
    if (!config.cleanerVariantId || config.cleanerVariantId === '0') return cart;
    let added = false;
    for (let attempt = 0; attempt < 20; attempt += 1) {
      // Adopt a single existing gift from this promotion without taking a paid unit.
      const legacy = cart.items.findIndex(item => !isGift(item) && String(item.variant_id) === String(config.cleanerVariantId) && item.quantity === 1 && item.final_line_price === 0 && item.line_level_discount_allocations?.some(allocation => allocation.discount_application?.title === 'SPEND $70+ FREE GIFT'));
      if (legacy !== -1) {
        cart = await request('cart/change.js', { line: legacy + 1, quantity: 1, properties: { ...cart.items[legacy].properties, _gba_reward: 'cleaner' } });
        continue;
      }
      const spend = cart.items.filter(item => !isProtection(item) && !isGift(item)).reduce((sum, item) => sum + item.final_line_price, 0);
      const eligible = cart.currency === 'AUD' && config.cleanerThreshold > 0 && spend >= config.cleanerThreshold;
      const gifts = cart.items.map((item, index) => ({ item, line: index + 1 })).filter(({ item }) => isGift(item));
      const keep = eligible ? gifts.find(({ item }) => String(item.variant_id) === String(config.cleanerVariantId) && !item.selling_plan_allocation) : null;
      const extra = gifts.find(entry => entry !== keep);
      if (extra) { cart = await change(extra.line, 0); continue; }
      if (keep) {
        if (keep.item.quantity !== 1) { cart = await change(keep.line, 1); continue; }
        if (keep.item.final_line_price !== 0) {
          cart = await removeManagedGifts(cart);
          giftSkipped = true;
          return cart;
        }
        return cart;
      }
      if (!eligible) return cart;
      if (added) throw new Error('The free gift could not be confirmed. Please try again.');
      if (!config.cleanerAvailable) throw new Error('The free gift is currently unavailable. Please contact us before checking out.');
      added = true;
      await request('cart/add.js', { items: [{ id: config.cleanerVariantId, quantity: 1, properties: { _gba_reward: 'cleaner' } }] });
      cart = await read();
    }
    throw new Error('The free gift could not be updated. Please try again.');
  }
  const reconcile = async cart => syncGift(await normalize(cart));

  async function request(path, body) {
    const response = await fetch(`${root}${path}`, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : 'Your cart could not be updated. Please try again.');
    return data;
  }
  const read = () => request('cart.js');
  const change = (line, quantity) => request('cart/change.js', { line, quantity });

  function message(text = '', error = false) {
    document.querySelectorAll(error ? '.gba-cart-error' : '.gba-cart-status').forEach(el => {
      el.textContent = text;
      el.hidden = !text;
    });
  }
  function setBusy(value) {
    busy = value;
    document.querySelectorAll('cart-form').forEach(form => {
      form.toggleAttribute('data-gba-busy', value);
      form.setAttribute('aria-busy', String(value));
      form.querySelectorAll('button:not(.js-close-drawer), input, select').forEach(el => {
        if (value && !el.disabled) { el.dataset.gbaLocked = 'true'; el.disabled = true; }
        else if (!value && el.dataset.gbaLocked) { el.disabled = false; delete el.dataset.gbaLocked; }
      });
    });
    if (!value) {
      const drawer = document.querySelector('cart-drawer[open]');
      if (drawer && typeof trapFocus === 'function') trapFocus(drawer, drawer.contains(document.activeElement) ? document.activeElement : drawer);
    }
  }

  // Correct existing protection only. Never opt a customer in during a refresh.
  async function normalize(cart, remove = false) {
    if (!remove && (!config.variantId || config.variantId === '0')) return cart;
    for (let attempt = 0; attempt < 20; attempt += 1) {
      const lines = cart.items.map((item, index) => ({ item, line: index + 1 })).filter(({ item }) => isProtection(item));
      const keep = remove ? null : lines.find(({ item }) => String(item.variant_id) === String(config.variantId) && !item.selling_plan_allocation);
      const extra = lines.find(entry => entry !== keep);
      if (extra) cart = await change(extra.line, 0);
      else if (keep && keep.item.quantity !== 1) cart = await change(keep.line, 1);
      else return cart;
    }
    throw new Error('Package Protection+ could not be updated. Please try again.');
  }

  async function render(cart) {
    const forms = [...document.querySelectorAll('cart-form[data-section-id]')];
    if (forms.length) {
      const sectionIds = [...new Set(forms.map(form => form.dataset.sectionId))];
      const url = new URL(location.href);
      url.searchParams.delete('section_id');
      url.searchParams.set('sections', sectionIds.join(','));
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Could not refresh your cart. Please try again.');
      const sections = await response.json();
      if (forms.some(form => typeof sections[form.dataset.sectionId] !== 'string')) throw new Error('Could not refresh your cart. Please reload before checking out.');
      const active = document.activeElement;
      const activeId = active?.id;
      const activeAction = active?.dataset.gbaCheckout;
      forms.forEach(form => form.refreshFromHtml(sections[form.dataset.sectionId]));
      document.querySelectorAll('.cart-link__count').forEach(el => { el.textContent = cart.item_count; });
      if (active && !active.isConnected) {
        const target = (activeId && document.getElementById(activeId)) || (activeAction && document.querySelector(`[data-gba-checkout="${activeAction}"]`)) || document.querySelector('cart-drawer[open]');
        if (target) target.focus();
      }
      // Refresh the native focus trap after controls are replaced.
      const drawer = document.querySelector('cart-drawer[open]');
      if (drawer && typeof trapFocus === 'function') trapFocus(drawer, drawer.contains(document.activeElement) ? document.activeElement : drawer);
    }
    setBusy(busy);
  }

  async function run(operation) {
    if (busy) return false;
    giftSkipped = false;
    setBusy(true);
    message('', true);
    message('Updating cart…');
    try {
      await operation();
      if (giftSkipped) {
        message(giftNotice, true);
        document.querySelectorAll('.gba-rewards > p').forEach(el => { el.textContent = 'Free gift unavailable with your current discounts.'; });
      }
      message('Cart updated.');
      return true;
    } catch (error) {
      // A failed response can still follow a successful server-side mutation.
      // Read again without retrying an ADD and without redirecting to checkout.
      try { await render(await read()); } catch (_) { /* Keep the error visible. */ }
      message(error.message || 'Your cart could not be updated. Please try again.', true);
      message('');
      return false;
    } finally {
      setBusy(false);
      if (refreshPending) { refreshPending = false; void refresh(); }
    }
  }
  function refresh() {
    if (busy) { refreshPending = true; return Promise.resolve(false); }
    return run(async () => render(await reconcile(await read())));
  }

  async function adjust(item, adjustment) {
    if (busy) return;
    const input = item.querySelector('.cart-item__quantity-input');
    let quantity = Number(input.value);
    if (adjustment.to !== undefined) quantity = adjustment.to;
    else if (adjustment.increase) quantity += 1;
    else if (adjustment.decrease) quantity -= 1;
    if (!Number.isInteger(quantity) || quantity < 0) {
      input.value = input.dataset.initialValue;
      message('Please enter a whole quantity of zero or more.', true);
      return;
    }
    if (input.max) quantity = Math.min(quantity, Number(input.max));
    const key = input.dataset.key;
    return run(async () => {
      let cart = await request('cart/change.js', { id: key, quantity });
      cart = await reconcile(cart);
      await render(cart);
    });
  }

  async function checkout(protectedPath, expectedTotal, keepProtection = false) {
    const terms = document.querySelector('[data-gba-terms], #terms');
    if (terms && !terms.checked) { message('Please agree to the Terms and Conditions before checkout.', true); terms.focus(); return; }
    return run(async () => {
      let cart = await syncGift(await read());
      if (protectedPath) {
        if (!config.available || !config.variantId || config.variantId === '0' || config.price !== 299 || cart.currency !== 'AUD' || !merchandiseCount(cart)) throw new Error('Package Protection+ is unavailable. Please use checkout without protection.');
        cart = await normalize(cart);
        if (!protectionLines(cart).length) {
          await request('cart/add.js', { items: [{ id: config.variantId, quantity: 1 }] });
        }
        // Verify authoritative state even when protection was already present.
        cart = await normalize(await read());
        const lines = protectionLines(cart);
        if (lines.length !== 1 || lines[0].quantity !== 1 || String(lines[0].variant_id) !== String(config.variantId)) throw new Error('Package Protection+ was not confirmed. Please try again.');
        if (lines[0].original_price !== 299) throw new Error('Package Protection+ price has changed. Please use checkout without protection.');
        if (cart.total_price !== expectedTotal) {
          await render(cart);
          throw new Error('Your cart total has changed. Please review the updated total and choose checkout again.');
        }
      } else if (!keepProtection) {
        cart = await normalize(cart, true);
        cart = await read();
        if (protectionLines(cart).length) throw new Error('Package Protection+ could not be removed. Please try again.');
      }
      cart = await syncGift(cart);
      if (!merchandiseCount(cart)) { await render(cart); throw new Error('Your cart is empty. Add an item before checkout.'); }
      window.location.assign(`${root}checkout`);
    });
  }

  window.GBACart = {
    get busy() { return busy; }, refresh, adjust,
    // Symmetry product forms share the lock; their existing add and rendering remain intact.
    beginNativeAdd() { if (busy) return false; setBusy(true); return true; },
    endNativeAdd() { setBusy(false); refreshPending = false; void refresh(); }
  };

  document.addEventListener('click', event => {
    const add = event.target.closest('[data-gba-add]');
    const checkoutButton = event.target.closest('[data-gba-checkout]');
    if (!add && !checkoutButton) return;
    event.preventDefault();
    if (busy) return;
    if (checkoutButton) void checkout(checkoutButton.dataset.gbaCheckout === 'protected', Number(checkoutButton.dataset.expectedTotal), checkoutButton.dataset.gbaCheckout === 'existing');
    else void run(async () => {
      const variantId = add.closest('.gba-upsell-card').querySelector('[data-gba-variant]')?.value || add.dataset.gbaAdd;
      if (String(variantId) === String(config.variantId)) return;
      await request('cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
      await render(await reconcile(await read()));
    });
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-gba-variant]')) {
      event.target.closest('.gba-upsell-card').querySelector('[data-gba-upsell-price]').textContent = event.target.selectedOptions[0].dataset.price;
    }
  });
  document.addEventListener('submit', event => {
    if (event.target.id !== 'cartform' || event.submitter?.name === 'update') return;
    event.preventDefault();
    if (!busy) void checkout(false, 0, true);
  });
  document.addEventListener('on:cart-drawer:before-open', () => { void refresh(); });
  window.addEventListener('pageshow', () => { void refresh(); });
})();
